import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Tag } from "@amzn/storm-ui";

import type { KeyValue } from "../../../../types/common";
import { DateFormat, formatDate } from "../../../../utils/date.utils";
import {
    convertAllCaps,
    convertAdditionalInfoValues,
} from "../../../../utils/unifiedContent.utils";

import {
    SpaceBetweenFlexBox,
    StyledAdditionalInfoText,
    StyledCardFooter,
    StyledCardHeader,
    StyledDescriptorContainer,
    StyledDescriptorPrimaryContextText,
    StyledDescriptorSecondaryContextText,
    StyledDivider,
    StyledFooterText,
    StyledInsightCard,
    StyledInsightCardBody,
    StyledTag,
} from "./styles";

interface CardLayoutV3Props {
    cardHeader: string;
    insightType?: string;
    insightTypeName?: string;
    primaryContext?: string; // Brand
    secondaryContext?: string; // Browse node
    additionalInfo?: KeyValue;
    lastModifiedDate?: string;
    tag?: string;
    dataTestId: string;
    actions: JSX.Element;
}

export const CardLayoutV3: React.FC<CardLayoutV3Props> = (props) => {
    const {
        cardHeader,
        insightType,
        insightTypeName,
        primaryContext,
        secondaryContext,
        additionalInfo,
        lastModifiedDate,
        tag,
        dataTestId,
        actions,
    } = props;

    const renderSecondaryContext = () => {
        if (insightType === InsightType.PITCHED_CONTENT) {
            return (
                <>
                    Opportunity:{" "}
                    <span style={{ textDecoration: "underline" }}>
                        {secondaryContext}
                    </span>{" "}
                    ({additionalInfo?.opportunityStatus})
                </>
            );
        }
        return (
            <span style={{ textDecoration: "underline" }}>
                {secondaryContext}
            </span>
        );
    };

    return (
        <StyledInsightCard data-testid={dataTestId} padding={"medium"}>
            <StyledInsightCardBody direction={"column"}>
                <StyledDescriptorContainer style={{ container: "insightCard" }}>
                    <SpaceBetweenFlexBox>
                        <StyledCardHeader data-testid={`${dataTestId}-header`}>
                            {cardHeader}
                        </StyledCardHeader>
                        {tag && <Tag variant="beta" label={tag} />}
                    </SpaceBetweenFlexBox>
                    <StyledTag
                        label={insightTypeName}
                        variant="info"
                        description="Description"
                    />
                    {primaryContext && (
                        <StyledDescriptorPrimaryContextText
                            data-testid={`${dataTestId}-primary`}
                        >
                            {convertAllCaps(primaryContext)}
                        </StyledDescriptorPrimaryContextText>
                    )}
                    {secondaryContext && (
                        <StyledDescriptorSecondaryContextText
                            data-testid={`${dataTestId}-secondary`}
                        >
                            {renderSecondaryContext()}
                        </StyledDescriptorSecondaryContextText>
                    )}
                    {additionalInfo &&
                        Object.entries(additionalInfo).map(
                            ([key, value]) =>
                                key !== "opportunityStatus" &&
                                value !== null &&
                                value !== undefined && (
                                    <StyledAdditionalInfoText
                                        key={key}
                                        data-testid={`${dataTestId}-${key.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        {`${convertAllCaps(key)}: ${convertAdditionalInfoValues(key, value)}`}
                                    </StyledAdditionalInfoText>
                                ),
                        )}
                </StyledDescriptorContainer>
                <StyledCardFooter>
                    {lastModifiedDate && (
                        <StyledFooterText data-testid={`${dataTestId}-footer`}>
                            Last updated:{" "}
                            {formatDate(
                                lastModifiedDate,
                                DateFormat.LONG_DATE_FORMAT,
                            )}
                        </StyledFooterText>
                    )}
                    <StyledDivider />
                    {actions}
                </StyledCardFooter>
            </StyledInsightCardBody>
        </StyledInsightCard>
    );
};
